import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/AuthContext';
import { BusinessProvider } from './components/BusinessContext';
import { Sidebar } from './components/Sidebar';
import { MobileHeader } from './components/MobileHeader';
import { TopHeader } from './components/TopHeader';
import { Discover } from './pages/Discover';
import { Shop } from './pages/Shop';
import { Help } from './pages/Help';
import { ListingDetail } from './pages/ListingDetail';
import { Requests } from './pages/Requests';
import { Messages } from './pages/Messages';
import { Saved } from './pages/Saved';
import { MyOrders } from './pages/MyOrders';
import { MyAI } from './pages/MyAI';
import { Profile } from './pages/Profile';
import { SellFlow } from './pages/SellFlow';
import { BusinessHub } from './pages/BusinessHub';
import { CartProvider } from './contexts/CartContext';
import { CartDrawer } from './components/cart/CartDrawer';
// Gates a route behind authentication. While signed out, it redirects to
// Discover and opens the login prompt so only Discover is usable.
function ProtectedRoute({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    isAuthenticated,
    openLogin
  } = useAuth();
  useEffect(() => {
    if (!isAuthenticated) openLogin();
  }, [isAuthenticated, openLogin]);
  if (!isAuthenticated) return <Navigate to="/" replace data-id="element-0" data-mp="2zlv84y2h77wn" />;
  return <>{children}</>;
}
type SearchSpeed = 'fast' | 'normal' | 'slow' | 'verySlow';
function AppShell({
  searchSpeed
}: {
  searchSpeed: SearchSpeed;
}) {
  const location = useLocation();
  // The storefront is a public page — render it without any app chrome
  // (no sidebar, top header, region selector, profile, or mobile nav).
  const isPublicShop = location.pathname.startsWith('/shop/');
  if (isPublicShop) {
    return <main className="app-min-fill flex flex-col min-h-screen bg-white" data-id="element-1" data-mp="nxwvhfx3tat2">
        <Routes data-id="element-2" data-mp="sssq8ntq6c9v">
          <Route path="/shop/:slug" element={<Shop data-id="element-4" data-mp="35yo8huu3i0e" />} data-id="element-3" data-mp="swgkxqawp4q6" />
        </Routes>
      </main>;
  }
  return <div className="app-min-fill flex min-h-screen bg-white" data-id="element-5" data-mp="38b7cycbvyf1">
      <Sidebar data-id="element-6" data-mp="3ubzvsgflbtha" />

      <div className="flex-1 min-w-0 flex flex-col relative overflow-x-hidden" data-id="element-7" data-mp="3ucj91juaptoz">
        <MobileHeader data-id="element-8" data-mp="d45sc040y5kz" />
        <TopHeader data-id="element-9" data-mp="d2l7x3bfc30u" />

        <main className="app-min-fill flex flex-col flex-1 overflow-x-hidden [&>*]:flex-1 [&>*]:min-h-0" data-id="element-10" data-mp="85l0anyxes7m">
          <Routes data-id="element-11" data-mp="3gl4qfe4avuig">
            <Route path="/" element={<Discover searchSpeed={searchSpeed} data-id="element-13" data-mp="3gj1sm7wwox36" />} data-id="element-12" data-mp="3gklce8hrtq9p" />
            <Route path="/listing/:id" element={<ProtectedRoute data-id="element-15" data-mp="3qbfztg6r6vwq">
                  <ListingDetail data-id="element-16" data-mp="3qcim1at4sa6b" />
                </ProtectedRoute>} data-id="element-14" data-mp="3bnmedeuzkz68" />
            <Route path="/requests" element={<ProtectedRoute data-id="element-18" data-mp="2xtv8cgggrxf9">
                  <Requests data-id="element-19" data-mp="2xssfw43htmx8" />
                </ProtectedRoute>} data-id="element-17" data-mp="3liwge2ldx6k0" />
            <Route path="/saved" element={<ProtectedRoute data-id="element-21" data-mp="18cq4thhcsarc">
                  <Saved data-id="element-22" data-mp="18boj8j2po5vd" />
                </ProtectedRoute>} data-id="element-20" data-mp="2svjvrxysb8tc" />
            <Route path="/orders" element={<ProtectedRoute data-id="element-24" data-mp="ygmsjissevqo">
                  <MyOrders data-id="element-25" data-mp="yhencrek8pfw" />
                </ProtectedRoute>} data-id="element-23" data-mp="1d62ln5fsjd9j" />
            <Route path="/messages" element={<ProtectedRoute data-id="element-27" data-mp="1r5am01nowrfb">
                  <Messages data-id="element-28" data-mp="1r2682ckfahbg" />
                </ProtectedRoute>} data-id="element-26" data-mp="13f7y7xqt67mi" />
            <Route path="/my-ai" element={<ProtectedRoute data-id="element-30" data-mp="1i2j8lwb97ll1">
                  <MyAI data-id="element-31" data-mp="1i5ninabehbjm" />
                </ProtectedRoute>} data-id="element-29" data-mp="1w2ki205l41c4" />
            <Route path="/business" element={<ProtectedRoute data-id="element-33" data-mp="6nclebjvfiww">
                  <BusinessHub data-id="element-34" data-mp="6qie00rpl2xd" />
                </ProtectedRoute>} data-id="element-32" data-mp="1m3lakkwk2bqp" />
            <Route path="/profile" element={<ProtectedRoute data-id="element-36" data-mp="qxxdhdnu0u2v">
                  <Profile data-id="element-37" data-mp="qz8h9dha21tz" />
                </ProtectedRoute>} data-id="element-35" data-mp="arc32xgw0nru" />
            <Route path="/sell" element={<ProtectedRoute data-id="element-39" data-mp="h8dpxup6cc1o">
                  <SellFlow data-id="element-40" data-mp="h59j0pkbr27b" />
                </ProtectedRoute>} data-id="element-38" data-mp="m549is06tpru" />
            <Route path="/help" element={<ProtectedRoute data-id="element-42" data-mp="19uygwu1qmm4p">
                  <Help data-id="element-43" data-mp="19tx70swgk5ok" />
                </ProtectedRoute>} data-id="element-41" data-mp="ca2i1pifwbm3" />
          </Routes>
        </main>
      </div>
    </div>;
}
export function App({
  /** Prototype review only: simulated search time — fast ≈2s, normal ≈5s, slow ≈10s, verySlow ≈15s. */
  searchSpeed = 'normal'
}: {
  searchSpeed?: 'fast' | 'normal' | 'slow' | 'verySlow';
}) {
  // Everyone can browse + chat freely. Registration is only required for
  // gated actions (e.g. viewing full listing details) via AuthProvider.
  // Business Hub activates adaptively via BusinessProvider.
  return <AuthProvider data-id="element-44" data-mp="1w5zz0gfj90mi">
      <BusinessProvider data-id="element-45" data-mp="1w2c4bdycq867">
        <CartProvider data-id="element-46" data-mp="205quuyjx26xw">
          <BrowserRouter data-id="element-47" data-mp="2058t5mehj0il">
            <AppShell searchSpeed={searchSpeed} data-id="element-48" data-mp="206jrv7t2pi2u" />
            <CartDrawer data-id="element-49" data-mp="255d837zqvn6p" />
          </BrowserRouter>
        </CartProvider>
      </BusinessProvider>
    </AuthProvider>;
}